import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import Button from '../../../../ui/canon/Button';
import { SparklesIcon, PauseIcon, BookOpenIcon, FileTextIcon, ChevronDownIcon, BarChart3Icon } from 'lucide-react';

// Helper to determine if a plan type includes assessment
const planTypeHasAssessment = (planType) => {
  // Only succession plans have an assessment component
  return planType === 'succession' || !planType;
};

// Helper to get plan type display name (returns key for i18n lookup)
const getPlanTypeKey = (planType) => {
  const typeKeys = {
    succession: 'succession.plans.plan_type_succession',
    catastrophic: 'succession.plans.plan_type_catastrophic',
    continuity: 'succession.plans.plan_type_continuity',
  };
  return typeKeys[planType] || 'succession.plans.plan_type_default';
};

const PlanBuilderHeader = ({ 
  progress = 0, 
  questionsAnswered = 0, 
  onPause, 
  onToggleDemo, 
  onViewResources, 
  onViewPlan,
  onViewResults,
  demoMode = false,
  hasResources = false,
  resourcesCount = 0,
  hasResponses = false,
  isPlanComplete = false,
  planType = 'succession',
  assistantName = 'AI Succession Assistant'
}) => {
  const intl = useIntl();
  const hasAssessment = planTypeHasAssessment(planType);
  const planTypeName = intl.formatMessage({ id: getPlanTypeKey(planType) });
  const [showPlanMenu, setShowPlanMenu] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowPlanMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return (
    <div 
      className="border-b border-succession-border bg-succession-bg-secondary px-6 py-2 flex-shrink-0"
      style={{ flex: '0 0 auto', minHeight: '52px' }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-succession-primary/10 flex items-center justify-center text-succession-primary flex-shrink-0">
            <SparklesIcon className="h-4 w-4" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-succession-text-primary leading-tight" style={{ fontSize: '15px' }}>{assistantName}</span>
            <span className="text-succession-text-secondary leading-tight" style={{ fontSize: '11px' }}>
              {intl.formatMessage({ id: 'succession.plans.header_progress' }, { percent: progress, count: questionsAnswered })}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Pause Button - hide when plan is complete */}
          {hasResponses && !isPlanComplete && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPause}
              className="gap-2 h-8"
            >
              <PauseIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.plans.button_pause' })}
            </Button>
          )}

          {/* Educational Resources Indicator - hide when count is 0 */}
          {hasResources && resourcesCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={onViewResources}
              className="gap-2 h-8"
              data-tour="learning-resources"
            >
              <BookOpenIcon className="h-4 w-4" />
              {intl.formatMessage({ id: 'succession.plans.learning_resources' })}
              <span className="ml-1 bg-succession-bg-secondary text-succession-text-primary text-[10px] px-1.5 py-0 rounded-full border border-succession-border min-w-[18px] text-center">
                {resourcesCount}
              </span>
            </Button>
          )}

          {/* View Plan Button - always visible, becomes dropdown when plan is complete AND has assessment */}
          <div className="relative" ref={menuRef} data-tour="view-plan">
            {isPlanComplete && hasAssessment ? (
              // For assessment plans: show dropdown with both options
              <>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowPlanMenu(!showPlanMenu)}
                  className="gap-2 h-8"
                >
                  <FileTextIcon className="h-4 w-4" />
                  {intl.formatMessage({ id: 'succession.plans.view_plan' })}
                  <ChevronDownIcon className="h-3 w-3 ml-1" />
                </Button>

                {showPlanMenu && (
                  <div className="absolute right-0 mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-succession-border z-50 py-1 animate-fade-in">
                    <button
                      onClick={() => {
                        setShowPlanMenu(false);
                        onViewPlan?.();
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-succession-hover-bg transition-colors"
                    >
                      <FileTextIcon className="h-4 w-4 text-succession-text-secondary" />
                      <div>
                        <div className="font-medium text-succession-text-primary">{planTypeName}</div>
                        <div className="text-xs text-succession-text-muted">{intl.formatMessage({ id: 'succession.plans.view_completed_document' })}</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowPlanMenu(false);
                        onViewResults?.();
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm flex items-center gap-3 hover:bg-succession-hover-bg transition-colors"
                    >
                      <BarChart3Icon className="h-4 w-4 text-succession-text-secondary" />
                      <div>
                        <div className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.assessment_and_results' })}</div>
                        <div className="text-xs text-succession-text-muted">{intl.formatMessage({ id: 'succession.plans.assessment_and_results_subtitle' })}</div>
                      </div>
                    </button>
                  </div>
                )}
              </>
            ) : isPlanComplete && !hasAssessment ? (
              // For prescriptive-only plans: single button that shows completed document
              <Button
                variant="primary"
                size="sm"
                onClick={onViewResults}
                className="gap-2 h-8"
              >
                <FileTextIcon className="h-4 w-4" />
                {intl.formatMessage({ id: 'succession.plans.view_plan_type' }, { planType: planTypeName })}
              </Button>
            ) : (
              // Not yet complete: simple view plan button
              <Button
                variant="primary"
                size="sm"
                onClick={onViewPlan}
                className="gap-2 h-8"
              >
                <FileTextIcon className="h-4 w-4" />
                {intl.formatMessage({ id: 'succession.plans.view_plan' })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanBuilderHeader;

